import React, { ComponentType, FC, createContext } from 'react';
import { Subtract } from 'utility-types';
import { findIndex, orderBy } from 'lodash';

import { Group, PostedToGroup, PublicUserProfile, UserFeedFollow } from 'api';
import { UserGroup } from 'types/app';
import { Queries } from 'components';
import { useClient, useCurrentUser, useTimeline } from 'hooks';
import { FOLLOW_QUERIES_LIMIT } from 'constants/index';

import { withApolloClient, IWithApolloClient } from './withApolloClient';

export type FollowedFilters = 'followed' | 'postable' | 'users' | 'groups';

type Postable = UserGroup[];
type Groups = Group[];
type Users = PublicUserProfile[];
type Follows = Groups & Users;

interface IPostable {
  hasTimeline: boolean;
  suggestedCount: number;
  remainingCount: number;
  list: Postable;
}

export interface IFollowed {
  postable: IPostable;
  groups: Groups;
  users: Users;
  followed: Follows;
}

export interface IWithFollowed {
  postable?: IPostable;
  groups?: Groups;
  users?: Users;
  followed?: Follows;
}

const FollowedContext = createContext<IWithFollowed>({
  postable: { hasTimeline: false, suggestedCount: 0, remainingCount: 0, list: [] },
  groups: [],
  users: [],
  followed: [],
});

const FollowedProvider: FC<IWithApolloClient> = withApolloClient(({ apolloClient, children }) => {
  const { disable_timeline } = useClient();
  const currentUser = useCurrentUser();
  const { timelineGroup } = useTimeline();

  function getAllFollowed(feedFollows: UserFeedFollow[]): Follows {
    const follows: Follows = [];

    feedFollows.forEach((follow: UserFeedFollow) => {
      follow.group && follows.push(follow.group);
      follow.user && follows.push(follow.user);
    });

    return follows;
  }

  function getPostable(feedFollows: UserFeedFollow[], postedToGroups: PostedToGroup[]): IPostable {
    const groups: Postable = [];

    feedFollows.forEach((follow: UserFeedFollow) => {
      follow.group && follow.postable && groups.push(follow.group);
    });

    const suggestedGroups = postedToGroups.reduce((list, postedToGroup) => {
      const suggestedGroupIndex = findIndex(groups, ['id', postedToGroup.group_id]);
      const suggestedGroup = groups.splice(suggestedGroupIndex, 1);
      return list.concat(suggestedGroup);
    }, []);
    const sortedGroups = orderBy(groups, [group => group.title.toLowerCase()], ['asc']);

    const combinedGroups = [...suggestedGroups, ...sortedGroups];

    if (!disable_timeline) {
      combinedGroups.unshift(timelineGroup);
    }

    return {
      hasTimeline: !disable_timeline,
      suggestedCount: suggestedGroups.length,
      remainingCount: sortedGroups.length,
      list: combinedGroups,
    };
  }

  function getFollowedUsers(feedFollows: UserFeedFollow[]): Users {
    const followedUsers: Users = [];

    feedFollows.forEach((follow: UserFeedFollow) => {
      follow.user && followedUsers.push(follow.user);
    });

    return followedUsers;
  }

  function getFollowedGroups(feedFollows: UserFeedFollow[]): Groups {
    const followedGroups: Groups = [];

    feedFollows.forEach((follow: UserFeedFollow) => {
      follow.group && followedGroups.push(follow.group);
    });

    return orderBy(followedGroups, [group => group.title.toLowerCase()], ['asc']);
  }

  return (
    <Queries.GetFollowed
      autoPaginate
      variables={{ user_id: currentUser.id, limit: FOLLOW_QUERIES_LIMIT }}>
      {({ followed }) => (
        <Queries.GetPostedToGroups>
          {postedToGroups => {
            const value: IFollowed = {
              followed: getAllFollowed(followed),
              postable: getPostable(followed, postedToGroups),
              users: getFollowedUsers(followed),
              groups: getFollowedGroups(followed),
            };

            return <FollowedContext.Provider value={value}>{children}</FollowedContext.Provider>;
          }}
        </Queries.GetPostedToGroups>
      )}
    </Queries.GetFollowed>
  );
});

const withFollowed = (filter: FollowedFilters) => <T extends {}>(
  ChildComponent: ComponentType<T>,
) => (props: Subtract<T, IWithFollowed>) => (
  <FollowedContext.Consumer>
    {(feedFollows: IFollowed) => {
      const followedProps: IWithFollowed = {
        [filter]: feedFollows[filter],
      };

      return <ChildComponent {...(props as T)} {...followedProps} />;
    }}
  </FollowedContext.Consumer>
);

export { withFollowed, FollowedContext, FollowedProvider };
